/* WorkingMaze — a continuously drawing random maze used while a tool is
   running. When fully drawn, it holds briefly, then un-draws from outer to
   inner, and regenerates into a new maze. The caption phrase types out
   character-by-character while the maze draws and erases right-to-left in
   sync with the retract. Adapted from design_handoff_daedalus/design_files/
   brand.jsx (grid backdrop + corner ticks removed, retract changed from
   slide-through to un-draw so the erase doesn't read as a bounce). */

import { useEffect, useMemo, useState } from 'react';
import { WORKING_PHRASES } from './phrases';

type Props = {
  size?: number;
  caption?: string;
  /** Where the phrase sits relative to the maze. */
  captionSide?: 'bottom' | 'right';
};

type Seg = { d: string; dist: number; len: number };

function buildMaze(): { segs: Seg[]; maxDist: number } {
  const W = 600,
    H = 600,
    CELL = 30,
    MARGIN = 20;
  const cx = W / 2,
    cy = H / 2;
  const cols = Math.floor((W - 2 * MARGIN) / CELL);
  const rows = Math.floor((H - 2 * MARGIN) / CELL);
  // Center the grid: cols*CELL rarely fills W - 2*MARGIN exactly, and
  // anchoring at MARGIN piles the slack onto the right/bottom, leaving
  // uneven gaps against the frame.
  const ox = (W - cols * CELL) / 2;
  const oy = (H - rows * CELL) / 2;
  const visited = Array.from({ length: rows }, () => new Array<boolean>(cols).fill(false));
  const walls: [number, number, number, number][] = [];
  const stack: [number, number][] = [[Math.floor(Math.random() * cols), Math.floor(Math.random() * rows)]];
  visited[stack[0][1]][stack[0][0]] = true;

  while (stack.length) {
    const [cxg, cyg] = stack[stack.length - 1];
    const dirs: [number, number][] = ([[1, 0], [-1, 0], [0, 1], [0, -1]] as [number, number][]).sort(
      () => Math.random() - 0.5
    );
    let moved = false;
    for (const [dx, dy] of dirs) {
      const nx = cxg + dx,
        ny = cyg + dy;
      if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
      if (visited[ny][nx]) continue;
      visited[ny][nx] = true;
      const ax = ox + cxg * CELL + CELL / 2;
      const ay = oy + cyg * CELL + CELL / 2;
      const bx = ox + nx * CELL + CELL / 2;
      const by = oy + ny * CELL + CELL / 2;
      walls.push([ax, ay, bx, by]);
      stack.push([nx, ny]);
      moved = true;
      break;
    }
    if (!moved) stack.pop();
  }

  // Frame with a gap on one side, hugging the grid extents so the inset
  // between frame and outermost passages is CELL/2 on every side.
  const FX0 = ox,
    FY0 = oy,
    FX1 = W - ox,
    FY1 = H - oy;
  const gapSide = Math.floor(Math.random() * 4); // 0=top, 1=right, 2=bottom, 3=left
  const GAP = 60;
  const frameSides: Array<() => [number, number, number, number][]> = [
    () => {
      if (gapSide !== 0) return [[FX0, FY0, FX1, FY0]];
      const cxgap = FX0 + (FX1 - FX0) / 2 + (Math.random() - 0.5) * 120;
      return [
        [FX0, FY0, cxgap - GAP / 2, FY0],
        [cxgap + GAP / 2, FY0, FX1, FY0],
      ];
    },
    () => {
      if (gapSide !== 1) return [[FX1, FY0, FX1, FY1]];
      const cygap = FY0 + (FY1 - FY0) / 2 + (Math.random() - 0.5) * 120;
      return [
        [FX1, FY0, FX1, cygap - GAP / 2],
        [FX1, cygap + GAP / 2, FX1, FY1],
      ];
    },
    () => {
      if (gapSide !== 2) return [[FX1, FY1, FX0, FY1]];
      const cxgap = FX0 + (FX1 - FX0) / 2 + (Math.random() - 0.5) * 120;
      return [
        [FX1, FY1, cxgap + GAP / 2, FY1],
        [cxgap - GAP / 2, FY1, FX0, FY1],
      ];
    },
    () => {
      if (gapSide !== 3) return [[FX0, FY1, FX0, FY0]];
      const cygap = FY0 + (FY1 - FY0) / 2 + (Math.random() - 0.5) * 120;
      return [
        [FX0, FY1, FX0, cygap + GAP / 2],
        [FX0, cygap - GAP / 2, FX0, FY0],
      ];
    },
  ];
  frameSides.forEach((fn) => fn().forEach((seg) => walls.push(seg)));

  const segs: Seg[] = walls.map(([x1, y1, x2, y2]) => {
    const mx = (x1 + x2) / 2,
      my = (y1 + y2) / 2;
    const dist = Math.hypot(mx - cx, my - cy);
    const len = Math.hypot(x2 - x1, y2 - y1);
    return { d: `M ${x1} ${y1} L ${x2} ${y2}`, dist, len };
  });
  const maxDist = Math.max(...segs.map((s) => s.dist));
  return { segs, maxDist };
}

// Timings (s)
const drawSpan = 2.38;
const drawDur = 0.47;
const holdDur = 0.6;
const retractSpan = 2.21;
const retractDur = 0.43;
const gapDur = 0.3;
const cycleMs = (drawSpan + drawDur + holdDur + retractSpan + retractDur + gapDur) * 1000;
const cycleS = cycleMs / 1000;
// Caption windows: type across the full draw, erase across the full retract.
const typeWin = drawSpan + drawDur;
const retractStartT = drawSpan + drawDur + holdDur;
const retractWin = retractSpan + retractDur;
// Keyframe percentage for a time offset within the cycle.
const k = (t: number) => ((Math.max(t, 0) / cycleS) * 100).toFixed(2);

export function WorkingMaze({ size = 360, caption, captionSide = 'bottom' }: Props) {
  const [gen, setGen] = useState(0);

  const phrase = useMemo(
    () => caption || WORKING_PHRASES[Math.floor(Math.random() * WORKING_PHRASES.length)],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gen, caption]
  );
  const chars = useMemo(() => Array.from(phrase), [phrase]);

  const { segs, maxDist } = useMemo(() => buildMaze(), [gen]);

  useEffect(() => {
    const t = setTimeout(() => setGen((g) => g + 1), cycleMs);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gen]);

  return (
    <div
      className={`working-maze wm-side-${captionSide}`}
      style={captionSide === 'right' ? { height: size } : { width: size, height: size }}
    >
      <svg key={gen} viewBox="0 0 600 600" width={size} height={size}>
        <g
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="square"
          strokeLinejoin="miter"
          className="wm-group"
        >
          {segs.map((s, i) => {
            const L = Math.max(s.len, 12);
            const animName = `wmCycle-${gen}-${i}`;
            return (
              <path
                key={i}
                d={s.d}
                className="wm-seg"
                style={{
                  strokeDasharray: L,
                  strokeDashoffset: L,
                  animation: `${animName} ${cycleS}s cubic-bezier(.65,.05,.35,1) forwards`,
                }}
              />
            );
          })}
        </g>
        <style>
          {segs
            .map((s, i) => {
              const L = Math.max(s.len, 12);
              const nd = s.dist / maxDist;
              const drawStart = nd * drawSpan;
              const drawEnd = drawStart + drawDur;
              const retractStart = retractStartT + (1 - nd) * retractSpan;
              const retractEnd = retractStart + retractDur;
              // Retract returns the dash to its start offset (un-draw) rather
              // than continuing to a negative offset — the slide-through made
              // segments visibly travel, which read as a bounce.
              return `@keyframes wmCycle-${gen}-${i}{
  0%, ${k(drawStart)}% { stroke-dashoffset: ${L}; }
  ${k(drawEnd)}%, ${k(retractStart)}% { stroke-dashoffset: 0; }
  ${k(retractEnd)}%, 100% { stroke-dashoffset: ${L}; }
}`;
            })
            .join('\n')}
        </style>
        <circle cx="300" cy="300" r="4" fill="#f3c94a" className="wm-heart" />
      </svg>
      <div className="wm-cap" key={`cap-${gen}`} aria-label={phrase}>
        {chars.map((c, i) => (
          <span key={i} style={{ animation: `wmCapC-${gen}-${i} ${cycleS}s linear both` }}>
            {c}
          </span>
        ))}
      </div>
      <style>
        {chars
          .map((_, i) => {
            const appear = ((i + 1) / chars.length) * typeWin;
            const vanish = retractStartT + ((chars.length - i) / chars.length) * retractWin;
            return `@keyframes wmCapC-${gen}-${i}{
  0%, ${k(appear - 0.02)}% { opacity: 0; }
  ${k(appear)}%, ${k(vanish - 0.02)}% { opacity: 1; }
  ${k(vanish)}%, 100% { opacity: 0; }
}`;
          })
          .join('\n')}
      </style>
      <style>{`
        .working-maze{
          position: relative;
          color: var(--accent);
          display: flex; align-items: center;
          animation: wmFadeIn .5s ease both;
        }
        .wm-side-bottom{ justify-content: center; }
        .wm-side-right{ gap: 14px; }
        @keyframes wmFadeIn { from { opacity: 0 } to { opacity: 1 } }
        .wm-seg { will-change: stroke-dashoffset; }
        .wm-heart { animation: wmHeart 1.3s ease-in-out infinite; }
        @keyframes wmHeart { 0%, 100% { opacity: 1 } 50% { opacity: .4 } }
        .wm-cap {
          font-family: 'JetBrains Mono', monospace;
          font-size: 10px; letter-spacing: .18em; text-transform: uppercase;
          color: var(--ink-3);
          white-space: pre;
        }
        .wm-cap span { opacity: 0; }
        .wm-side-bottom .wm-cap {
          position: absolute; bottom: -22px; left: 0; right: 0; text-align: center;
        }
      `}</style>
    </div>
  );
}
